import { useEffect, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useBrand } from "@/context/BrandContext";
import { api } from "@/lib/api";
import { Logo } from "@/components/Logo";
import GameweekBanner from "@/components/GameweekBanner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

export default function RegisterPage() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { brand, setBrandBySlug } = useBrand();
  // Slug sources in priority order (any one is sufficient):
  //   1. `/c/:slug/register` — the scoped route (URL param)
  //   2. `?client=<slug>` — legacy query param that older invites still use
  //   3. `brand.slug` — set by BrandContext (e.g. after visiting `/[slug]`)
  const { slug: routeSlug } = useParams();
  const clientSlugParam = searchParams.get("client");
  const effectiveClientSlug = routeSlug || clientSlugParam || brand?.slug || null;
  // Referrer name (?ref=…) from a personal invite link. When present we swap
  // the "Create account." headline for a warm personalised welcome and
  // pre-check the ToS box so the flow feels one-tap friendly.
  const inviterRaw = searchParams.get("ref");
  const inviterName = inviterRaw ? decodeURIComponent(inviterRaw).slice(0, 60).trim() : "";
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [agreed, setAgreed] = useState(false);
  const [is18, setIs18] = useState(false);
  const [wantsWhatsapp, setWantsWhatsapp] = useState(false);
  const [phone, setPhone] = useState("");
  const [loading, setLoading] = useState(false);
  const [sponsorship, setSponsorship] = useState(null);

  // Adopt any client slug from the URL / query / route param as the active
  // brand so the branded chrome (logo, colours, sponsors) applies during
  // registration.
  useEffect(() => {
    const seedSlug = routeSlug || clientSlugParam;
    if (seedSlug) {
      setBrandBySlug(seedSlug);
    }
  }, [routeSlug, clientSlugParam, setBrandBySlug]);

  // Fetch the editor-configured sponsorship (public endpoint). If it errors
  // or is disabled, we fall back to the default stadium hero.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/registration-sponsorship");
        if (!cancelled) setSponsorship(data);
      } catch (err) {
        // Non-critical — register page still works with the default hero on failure.
        if (typeof console !== "undefined") console.debug("[register] sponsorship fetch failed:", err?.message);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!agreed) {
      toast.error("Please agree to the Terms of Service and Privacy Policy");
      return;
    }
    if (!is18) {
      toast.error("You must confirm you are 18 or over");
      return;
    }
    setLoading(true);
    const res = await register(name, email, password, {
      age_confirmed_18: true,
      wants_whatsapp: wantsWhatsapp,
      phone_e164: phone.trim() || null,
      client_slug: effectiveClientSlug,
    });
    setLoading(false);
    if (res.ok) {
      toast.success(
        wantsWhatsapp
          ? "Account created — check your email for the WhatsApp group link"
          : "Account created",
      );
      // Keep newly-registered pub users inside their branded experience —
      // fall through to `/` only when there's no active pub brand
      // (companion bug-fix to LoginPage post-login redirect, 2026-08).
      const target = effectiveClientSlug ? `/c/${effectiveClientSlug}` : "/";
      navigate(target, { replace: true });
    } else {
      toast.error(res.error);
    }
  };

  // Global Dugsy sponsorship (org logo, message, sponsor tiles) is master-platform
  // content and MUST NOT leak onto a client-branded register (e.g. `/register?client=…`
  // or when brand context was set by a prior `/[slug]` visit). Each pub renders ONLY
  // its own sponsors — falling back to hidden if it has none of its own.
  const showSponsorship =
    !brand &&
    !!sponsorship?.enabled &&
    (
      !!sponsorship.message ||
      !!sponsorship.organisation_logo_url ||
      (sponsorship.sponsors && sponsorship.sponsors.length > 0)
    );

  // For a branded register, the visual panel uses the pub's own logo as a
  // watermark (their record's asset — no Dugsy/global content). If the pub
  // has no logo either, we hide the entire panel and render the form only,
  // matching the user's rule: "hide rather than fall back to globals".
  const brandLogoBg = brand
    ? (brand.logo_url
        ? (brand.logo_url.startsWith("http") ? brand.logo_url : `${process.env.REACT_APP_BACKEND_URL}${brand.logo_url}`)
        : brand.logo_data_url) || null
    : null;
  const pubSponsors = brand?.sponsors || [];
  const pubSponsorshipMessage = brand?.sponsorship_message || "";
  const hasPubSponsorContent = !!brand && (pubSponsors.length > 0 || !!pubSponsorshipMessage);
  const showBrandPanel = !!brand && (!!brandLogoBg || hasPubSponsorContent);

  return (
    <div className={`min-h-screen bg-white ${(showSponsorship || showBrandPanel || !brand) ? "grid lg:grid-cols-2" : ""}`}>
      {showSponsorship ? (
        <div
          className="flex relative bg-zinc-950 text-white"
          data-testid="register-sponsorship-panel"
        >
          <div className="relative flex flex-col justify-between p-6 sm:p-10 xl:p-14 w-full gap-8">
            <div>
              {sponsorship.organisation_logo_url && (
                <img
                  src={sponsorship.organisation_logo_url}
                  alt="Organisation logo"
                  data-testid="register-sponsorship-logo"
                  className="h-20 sm:h-24 w-auto max-w-[220px] object-contain bg-white/95 p-3 rounded-sm mx-auto sm:mx-0"
                />
              )}
              {sponsorship.message && (
                <p
                  data-testid="register-sponsorship-message"
                  className="mt-6 sm:mt-8 font-heading text-2xl sm:text-3xl xl:text-4xl font-bold leading-[1.15] tracking-tight max-w-lg text-center sm:text-left"
                >
                  {sponsorship.message}
                </p>
              )}
            </div>

            {sponsorship.sponsors?.length > 0 && (
              <div>
                <p className="text-[10px] font-bold uppercase tracking-[0.28em] text-white/60 text-center sm:text-left">
                  With thanks to our sponsors
                </p>
                <div
                  className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-3 max-w-md mx-auto sm:mx-0"
                  data-testid="register-sponsorship-list"
                >
                  {sponsorship.sponsors.map((s, i) => {
                    const inner = (
                      <img
                        src={s.image_url}
                        alt={s.alt_text || `Sponsor ${i + 1}`}
                        className="h-20 w-full object-contain bg-white/95 rounded-sm p-2"
                      />
                    );
                    const stableKey = s.image_url || `sponsor-${i}`;
                    return s.target_url ? (
                      <a
                        key={stableKey}
                        href={s.target_url}
                        target="_blank"
                        rel="noreferrer noopener"
                        data-testid={`register-sponsorship-link-${i}`}
                        className="block transition-transform hover:scale-[1.03]"
                      >
                        {inner}
                      </a>
                    ) : (
                      <div key={stableKey} data-testid={`register-sponsorship-item-${i}`}>{inner}</div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>
      ) : showBrandPanel ? (
        <div
          className="hidden lg:flex relative overflow-hidden"
          style={{ backgroundColor: "#F5F5F5" }}
          data-testid="register-brand-panel"
        >
          {brandLogoBg && (
            <img
              src={brandLogoBg}
              alt=""
              aria-hidden
              data-testid="register-brand-watermark"
              className="pointer-events-none select-none absolute inset-0 w-full h-full object-cover opacity-40"
            />
          )}
          {hasPubSponsorContent && (
            <div className="relative flex flex-col justify-between p-10 xl:p-14 w-full gap-8 z-10">
              {pubSponsorshipMessage && (
                <p
                  data-testid="register-pub-sponsorship-message"
                  className="font-heading text-2xl xl:text-3xl font-bold leading-[1.15] tracking-tight max-w-lg text-zinc-950"
                >
                  {pubSponsorshipMessage}
                </p>
              )}
              {pubSponsors.length > 0 && (
                <div>
                  <p className="text-[10px] font-bold uppercase tracking-[0.28em] text-zinc-600">
                    Local sponsors
                  </p>
                  <ul
                    className="mt-4 flex flex-col gap-4"
                    data-testid="register-pub-sponsorship-list"
                  >
                    {pubSponsors.map((s, i) => {
                      const img = (
                        <img
                          src={s.image_url}
                          alt={s.alt_text || `Sponsor ${i + 1}`}
                          className="h-16 w-16 object-contain bg-transparent rounded-sm p-2"
                        />
                      );
                      const label = s.alt_text ? (
                        <span className="font-semibold text-sm text-zinc-900 flex-1 min-w-0">{s.alt_text}</span>
                      ) : null;
                      const key = s.image_url || `pub-sponsor-${i}`;
                      return (
                        <li key={key} data-testid={`register-pub-sponsorship-cell-${i}`}>
                          {s.target_url ? (
                            <a
                              href={s.target_url}
                              target="_blank"
                              rel="noreferrer noopener"
                              data-testid={`register-pub-sponsorship-link-${i}`}
                              className="flex items-center gap-3 hover:scale-[1.01] transition-transform"
                            >
                              {img}
                              {label}
                            </a>
                          ) : (
                            <div className="flex items-center gap-3" data-testid={`register-pub-sponsorship-item-${i}`}>
                              {img}
                              {label}
                            </div>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>
      ) : !brand ? (
        <div
          className="hidden lg:block relative bg-cover bg-center"
          style={{
            backgroundImage:
              "url('https://images.unsplash.com/photo-1661632359980-9896d88b9b7b?crop=entropy&cs=srgb&fm=jpg&q=80&w=1400')",
          }}
          aria-hidden="true"
          data-testid="register-stadium-hero"
        >
          <div className="absolute inset-0 bg-zinc-950/30" />
          <div className="absolute bottom-12 left-12 right-12 text-white">
            <p className="font-heading text-5xl font-bold leading-[1.05] tracking-tight">
              Beat your friends<br />at the stats game.
            </p>
            <p className="mt-4 text-sm uppercase tracking-[0.25em] text-white/80">
              3 pts exact · 1 pt within ±1 · 2 pts within ±5 (throw-ins)
            </p>
          </div>
        </div>
      ) : null}

      <div className="flex flex-col justify-center px-6 sm:px-12 lg:px-20 py-12">
        <div className="mb-10">
          <Logo variant="large" />
        </div>
        {brand && (
          <div
            className="mb-8 flex items-center gap-3 p-4 rounded-sm border"
            style={{ borderColor: `${brand.primary_color}66`, backgroundColor: `${brand.primary_color}0F` }}
            data-testid="register-brand-banner"
          >
            {brand.logo_data_url ? (
              <img src={brand.logo_data_url} alt={brand.name} className="h-10 w-10 rounded-sm object-contain bg-white p-0.5 border border-zinc-200" />
            ) : (
              <div className="h-10 w-10 rounded-sm flex items-center justify-center text-white text-sm font-bold" style={{ backgroundColor: brand.primary_color }}>
                {brand.name.slice(0, 1)}
              </div>
            )}
            <div>
              <p className="text-[10px] font-bold uppercase tracking-[0.2em]" style={{ color: brand.primary_color }}>
                Joining
              </p>
              <p className="text-sm font-semibold text-zinc-950">{brand.name}</p>
            </div>
          </div>
        )}
        <div className="max-w-sm">
          {/* iter143 — Automated Gameweek Engine yellow banner mirrors the
              login-side placement. Sits above the invite banner + form so
              new joiners see gameweek context immediately. Returns null
              when the client hasn't set gameweek/match/day counts. */}
          <div className="mb-6">
            <GameweekBanner slugOverride={brand?.slug || null} variant="stacked" />
          </div>
          {inviterName && (
            <div
              data-testid="register-invite-banner"
              className="mb-6 rounded-sm border p-4 flex items-start gap-3"
              style={{
                borderColor: brand?.primary_color ? `${brand.primary_color}66` : "#0038FF33",
                backgroundColor: brand?.primary_color ? `${brand.primary_color}0F` : "#0038FF0A",
              }}
            >
              <div
                className="mt-0.5 h-9 w-9 shrink-0 rounded-full flex items-center justify-center text-white text-sm font-bold"
                style={{ backgroundColor: brand?.primary_color || "#0038FF" }}
                aria-hidden="true"
              >
                {inviterName.slice(0, 1).toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <p
                  className="text-[10px] font-bold uppercase tracking-[0.2em]"
                  style={{ color: brand?.primary_color || "#0038FF" }}
                  data-testid="register-invite-banner-label"
                >
                  You&apos;ve been invited
                </p>
                <p className="mt-0.5 text-sm text-zinc-950 leading-snug" data-testid="register-invite-banner-message">
                  <span className="font-semibold">{inviterName}</span> wants you to join{" "}
                  {brand ? (
                    <span className="font-semibold">{brand.name}</span>
                  ) : (
                    <span className="font-semibold">Dugsy&apos;s Football Challenge</span>
                  )}
                  . Sign up in 30 seconds — take them on today.
                </p>
              </div>
            </div>
          )}
          <h1
            className="font-heading text-4xl sm:text-5xl font-bold tracking-tight text-zinc-950"
            data-testid="register-heading"
          >
            {inviterName ? "Take the challenge." : "Create account."}
          </h1>
          <p className="mt-3 text-base text-zinc-500" data-testid="register-subheading">
            {inviterName
              ? `Beat ${inviterName} at the stats game — starts with today's match-day.`
              : "Join the Dugsy global league in 30 seconds."}
          </p>

          <form onSubmit={onSubmit} className="mt-10 space-y-5" data-testid="register-form">
            {/* iter172 — When the user hit master `/register` with no pub
                context, warn them BEFORE they submit and disable the form.
                The backend also rejects with a 400, but this saves a
                round-trip and gives clearer guidance. */}
            {!effectiveClientSlug && (
              <div
                data-testid="register-no-slug-warning"
                className="rounded-sm border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900 leading-snug"
              >
                <p className="font-semibold">
                  You&apos;re on the master sign-up page.
                </p>
                <p className="mt-1">
                  Please sign up from your pub&apos;s own page — the URL will
                  look like <span className="font-mono text-xs">play.amblemead.biz/&lt;your-pub-slug&gt;</span>.
                  If you&apos;re not sure which pub to use, ask the person
                  who shared the link.
                </p>
              </div>
            )}
            <div>
              <Label htmlFor="name" className="text-xs font-bold uppercase tracking-[0.15em] text-zinc-500">Display name</Label>
              <Input
                id="name"
                required
                value={name}
                onChange={(e) => setName(e.target.value)}
                data-testid="register-name-input"
                className="mt-2 h-12 rounded-sm border-zinc-300 focus-visible:ring-[#0038FF]"
              />
            </div>
            <div>
              <Label htmlFor="email" className="text-xs font-bold uppercase tracking-[0.15em] text-zinc-500">Email</Label>
              <Input
                id="email"
                type="email"
                required
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="register-email-input"
                className="mt-2 h-12 rounded-sm border-zinc-300 focus-visible:ring-[#0038FF]"
              />
            </div>
            <div>
              <Label htmlFor="password" className="text-xs font-bold uppercase tracking-[0.15em] text-zinc-500">Password</Label>
              <Input
                id="password"
                type="password"
                required
                minLength={6}
                autoComplete="new-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid="register-password-input"
                className="mt-2 h-12 rounded-sm border-zinc-300 focus-visible:ring-[#0038FF]"
              />
              <p className="mt-1 text-xs text-zinc-400">Minimum 6 characters.</p>
            </div>

            {/* Optional WhatsApp banter group opt-in */}
            <div className="rounded-sm border border-emerald-200 bg-emerald-50/40 p-3" data-testid="register-whatsapp-block">
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={wantsWhatsapp}
                  onChange={(e) => setWantsWhatsapp(e.target.checked)}
                  data-testid="register-whatsapp-checkbox"
                  className="mt-0.5 h-4 w-4 rounded-sm border-zinc-400 text-emerald-600 focus:ring-emerald-600 cursor-pointer"
                />
                <span className="text-xs text-zinc-700 leading-relaxed">
                  Add me to the <span className="font-semibold text-zinc-950">Dugsy&apos;s Football Challenge</span> WhatsApp banter group.
                  <span className="block mt-0.5 text-zinc-500">We&apos;ll email you the group invite link after sign-up.</span>
                </span>
              </label>
              {wantsWhatsapp && (
                <div className="mt-3 pl-7">
                  <Label htmlFor="phone" className="text-[10px] font-bold uppercase tracking-[0.18em] text-zinc-500">Phone number (optional)</Label>
                  <Input
                    id="phone"
                    type="tel"
                    autoComplete="tel"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    placeholder="+44 7123 456789"
                    data-testid="register-phone-input"
                    className="mt-1 h-10 rounded-sm border-zinc-300 text-sm"
                  />
                  <p className="mt-1 text-[11px] text-zinc-500">
                    So we can add you to the group manually if you don&apos;t click the email link.
                  </p>
                </div>
              )}
            </div>

            <label className="flex items-start gap-3 cursor-pointer" data-testid="register-age-row">
              <input
                type="checkbox"
                checked={is18}
                onChange={(e) => setIs18(e.target.checked)}
                required
                data-testid="register-age-checkbox"
                className="mt-0.5 h-4 w-4 rounded-sm border-zinc-400 text-[#0038FF] focus:ring-[#0038FF] cursor-pointer"
              />
              <span className="text-xs text-zinc-500 leading-relaxed">
                I confirm I am <span className="font-semibold text-zinc-950">18 years of age or older</span>.
              </span>
            </label>
            <label className="flex items-start gap-3 cursor-pointer" data-testid="register-terms-row">
              <input
                type="checkbox"
                checked={agreed}
                onChange={(e) => setAgreed(e.target.checked)}
                required
                data-testid="register-terms-checkbox"
                className="mt-0.5 h-4 w-4 rounded-sm border-zinc-400 text-[#0038FF] focus:ring-[#0038FF] cursor-pointer"
              />
              <span className="text-xs text-zinc-500 leading-relaxed">
                I agree to the{" "}
                <Link to={effectiveClientSlug ? `/c/${effectiveClientSlug}/terms` : "/terms"} target="_blank" rel="noreferrer" data-testid="register-terms-link" className="font-semibold text-zinc-950 underline underline-offset-2 hover:text-[#0038FF]">
                  Terms of Service
                </Link>{" "}
                and{" "}
                <Link to={effectiveClientSlug ? `/c/${effectiveClientSlug}/privacy` : "/privacy"} target="_blank" rel="noreferrer" data-testid="register-privacy-link" className="font-semibold text-zinc-950 underline underline-offset-2 hover:text-[#0038FF]">
                  Privacy Policy
                </Link>.
              </span>
            </label>
            <Button
              type="submit"
              disabled={loading || !agreed || !is18 || !effectiveClientSlug}
              data-testid="register-submit-btn"
              className="w-full h-12 rounded-sm text-white font-semibold tracking-tight active:scale-[0.98] transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
              style={{ backgroundColor: brand?.primary_color || "#0038FF" }}
            >
              {loading ? "Creating…" : brand ? `Join ${brand.name}` : "Create account"}
            </Button>
          </form>

          <p className="mt-8 text-sm text-zinc-500">
            Already on Dugsy?{" "}
            <Link to={effectiveClientSlug ? `/${effectiveClientSlug}` : "/login"} data-testid="login-link" className="font-semibold text-zinc-950 underline underline-offset-4 decoration-2 hover:text-[#0038FF] hover:decoration-[#0038FF]">
              Sign in
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
